/* eslint-disable arrow-body-style */

import moment, { Moment } from 'moment';
import type { IDateType } from '@gp/components/RangePicker';
import { COMMON_DATE_TYPES, DATE_TYPE } from '@gp/components/RangePicker';
import { SortType } from '@gp/enums/common';

import type { INullableMoment } from '@home/types/common';

export const GRANULARITY_MAPPINGS: Record<IDateType, string> = {
  date: 'day',
  week: 'week',
  month: 'month',
  day: 'day',
  Month: 'month',
  Week: 'week',
  Day: 'day',
  Date: 'day',
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  year: 'year',
  Year: 'year',
  yearly: 'year',
  Yearly: 'year',
};

// 继承-日期类型格式转换成正常格式
export const GRANULARITY_FILTER_MAPPINGS: any = {
  day: 'date',
  date: 'date',
  week: 'week',
  month: 'month',
  Month: 'month',
  Week: 'week',
  Day: 'date',
  Date: 'date',
  monthly: 'month',
  weekly: 'week',
  daily: 'date',
  year: 'year',
  Year: 'year',
  yearly: 'year',
  Yearly: 'year',
} as any;

// 所有日期类型格式转换成ly格式
export const GRANULARITY_LY_MAPPINGS: { [key: string]: any } = {
  day: 'daily',
  date: 'daily',
  week: 'weekly',
  month: 'monthly',
  Month: 'monthly',
  Week: 'weekly',
  Day: 'daily',
  Date: 'daily',
  monthly: 'monthly',
  weekly: 'weekly',
  daily: 'daily',
  year: 'yearly',
  Year: 'yearly',
  yearly: 'yearly',
  Yearly: 'yearly',
} as any;

const GRANULARITY_MOMENT: Record<IDateType, moment.unitOfTime.StartOf> = {
  date: 'day',
  week: 'week',
  month: 'month',
};

const GRANULARITY_PLURAL_MOMENT: Record<IDateType, moment.unitOfTime.DurationConstructor> = {
  date: 'days',
  week: 'weeks',
  month: 'months',
};

/**
 * 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 * @param date 日期
 * @returns 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 */
export const getNullableMoment = (date?: string): INullableMoment => {
  if (!date) return null;
  const dateM = moment(date);
  return dateM?.isValid() ? dateM : null;
};

const formatDateStrFromMoment = (m?: Moment, pattern = 'YYYY-MM-DD'): string => {
  if (!m?.isValid()) return '';
  return m.format(pattern);
};

const GRANULARITY_LIST = Object.values(GRANULARITY_MAPPINGS);

export const getReqGranularity = (dateType?: IDateType, defaultReqGranularity = GRANULARITY_MAPPINGS.date): string => {
  const validDefaultReqGranularity = GRANULARITY_LIST.includes(defaultReqGranularity)
    ? defaultReqGranularity
    : GRANULARITY_MAPPINGS.date;
  if (!dateType) return validDefaultReqGranularity;
  return GRANULARITY_MAPPINGS[dateType] || validDefaultReqGranularity;
};

/**
 * 获取有效的日期粒度
 * @param granularity 日期粒度
 * @param granularities 日期粒度集合
 * @returns 有效的日期粒度
 */
export const getValidGranularity = (granularity?: IDateType, granularities = COMMON_DATE_TYPES): IDateType => {
  const defaultType =
    Array.isArray(granularities) && granularities?.length > 0
      ? GRANULARITY_FILTER_MAPPINGS[granularities[0]]
      : DATE_TYPE;
  if (!granularity) return defaultType;
  return granularities?.includes(granularity) ? granularity : defaultType;
};

/**
 * 获取RangePicker组件（home仓）的disabledDate配置中的某个日期粒度的回调方法
 * 注意返回的方法内，返回true时，代表当前日期可选，否则禁用当前日期
 * @param dateType 日期粒度
 * @param minDate 最小日期
 * @param maxDate 最大日期
 * @returns RangePicker组件（home仓）的disabledDate配置中的某个日期粒度的回调方法
 */
export const getDisabledDateByDateType: (
  dateType: moment.unitOfTime.StartOf,
  minDate?: string,
  maxDate?: string,
) => (m?: Moment) => boolean = (dateType, minDate, maxDate) => {
  return (m) => {
    if (!m?.isValid()) return false;
    const minDateM: INullableMoment = !minDate ? null : moment(minDate).startOf(dateType);
    const maxDateM: INullableMoment = !maxDate ? null : moment(maxDate).endOf(dateType);
    const isMinDateMValid = !!minDateM?.isValid();
    const isMaxDateMValid = !!maxDateM?.isValid();
    if (!isMinDateMValid && !isMaxDateMValid) return true;
    if (isMinDateMValid && isMaxDateMValid) return m.isSameOrAfter(minDateM) && m.isSameOrBefore(maxDateM);
    if (isMinDateMValid) return m.isSameOrAfter(minDateM);
    return m.isSameOrBefore(maxDateM);
  };
};

const getSortedDateRangeMoment = (moments?: Moment[], sortType = SortType.Asc): Moment[] => {
  if (!Array.isArray(moments) || moments.length === 0) return [];
  return moments.sort((a, b) => {
    if (a.isSame(b)) return 0;
    if (sortType === SortType.Asc) return a.isAfter(b) ? 1 : -1;
    if (sortType === SortType.Desc) return a.isBefore(b) ? 1 : -1;
    return 0;
  });
};

export const getValidDateRange = (params?: {
  granularity?: IDateType;
  granularities?: IDateType[];
  startDate?: string;
  endDate?: string;
  minDate?: string;
  maxDate?: string;
}): {
  granularity: IDateType;
  startDate: string;
  endDate: string;
} => {
  const { granularity = 'date', granularities, startDate, endDate, minDate, maxDate } = params || {};
  const startDateTmp = startDate || minDate || '';
  const endDateTmp = endDate || maxDate || '';
  const validGranularity = getValidGranularity(granularity, granularities);
  let startDateTmpM = getNullableMoment(startDateTmp);
  let endDateTmpM = getNullableMoment(endDateTmp);

  // startDate、endDate无法兜底时，直接返回空字符串
  if (startDateTmpM === null || endDateTmpM === null) {
    return { granularity: validGranularity, startDate: '', endDate: '' };
  }

  // 确保startDate小于endDate（防止入参异常时，startDate大于endDate）
  [startDateTmpM, endDateTmpM] = getSortedDateRangeMoment([startDateTmpM, endDateTmpM]);
  startDateTmpM?.startOf(validGranularity as any);
  endDateTmpM?.endOf(validGranularity as any);

  let minDateM = getNullableMoment(minDate);
  let maxDateM = getNullableMoment(maxDate);

  // minDate、maxDate都异常时，无法限制startDate、endDate，直接返回startDate、endDate
  if (minDateM === null && maxDateM === null) {
    return {
      granularity: validGranularity,
      startDate: formatDateStrFromMoment(startDateTmpM),
      endDate: formatDateStrFromMoment(endDateTmpM),
    };
  }

  // minDate、maxDate同时有效时，startDate、endDate需要同时满足minDate、maxDate
  if (minDateM !== null && maxDateM !== null) {
    [minDateM, maxDateM] = getSortedDateRangeMoment([minDateM, maxDateM]);
    minDateM?.startOf(validGranularity as any);
    maxDateM?.endOf(validGranularity as any);
    return {
      granularity: validGranularity,
      startDate: formatDateStrFromMoment(
        moment.min(moment.max(startDateTmpM, minDateM), maxDateM).startOf(validGranularity as any),
      ),
      endDate: formatDateStrFromMoment(
        moment.max(moment.min(endDateTmpM, maxDateM), minDateM).endOf(validGranularity as any),
      ),
    };
  }

  // 仅minDate有效时，startDate、endDate不能早于minDate
  if (minDateM !== null) {
    minDateM?.startOf(validGranularity as any);
    return {
      granularity: validGranularity,
      startDate: formatDateStrFromMoment(moment.max(startDateTmpM, minDateM).startOf(validGranularity as any)),
      endDate: formatDateStrFromMoment(moment.max(endDateTmpM, minDateM).endOf(validGranularity as any)),
    };
  }

  // 仅maxDate有效时，endDate不能晚于maxDate
  maxDateM?.endOf(validGranularity as any);
  return {
    granularity: validGranularity,
    startDate: formatDateStrFromMoment(moment.min(startDateTmpM, maxDateM as Moment).startOf(validGranularity as any)),
    endDate: formatDateStrFromMoment(moment.min(endDateTmpM, maxDateM as Moment).endOf(validGranularity as any)),
  };
};

/**
 * 根据指定的日期粒度、开始日期、结束日期，生成从开始日期到结束日期之间的日期
 * @param granularity 日期粒度
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @param sortType 日期排序方式 默认按日期升序
 * @return 日期列表
 */
export const getDatesByRange = (
  granularity?: IDateType,
  startDate?: string,
  endDate?: string,
  sortType = SortType.Asc,
): string[] => {
  const validGranularity = !!granularity && COMMON_DATE_TYPES.includes(granularity) ? granularity : DATE_TYPE;
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);

  if (!startDateM || !endDateM) return [];

  const dates: string[] = [];
  const validGranularityMoment = GRANULARITY_MOMENT[validGranularity];
  const validGranularityPluralMoment = GRANULARITY_PLURAL_MOMENT[validGranularity];
  const startDateMTmp = startDateM.clone().startOf(validGranularityMoment);
  const endDateMTmp = endDateM.clone().endOf(validGranularityMoment);

  if (sortType === SortType.Desc) {
    while (endDateMTmp.isSameOrAfter(startDateMTmp, validGranularityMoment)) {
      dates.push(formatDateStrFromMoment(endDateMTmp.clone().startOf(validGranularityMoment)));
      endDateMTmp.subtract(1, validGranularityPluralMoment);
    }
  } else {
    while (startDateMTmp.isSameOrBefore(endDateMTmp, validGranularityMoment)) {
      dates.push(formatDateStrFromMoment(startDateMTmp));
      startDateMTmp.add(1, validGranularityPluralMoment);
    }
  }

  return dates;
};

// 通用方法，获取月粒度，最近一个月的时间
export const getMonthFromtTime = (seNum: number) => {
  const endDateM = moment();
  const startDateM = moment(endDateM).subtract('months', 1);
  if (seNum === 1) {
    return endDateM.endOf('month').format('YYYY-MM-DD');
  }
  return startDateM.startOf('month').format('YYYY-MM-DD');
};
